import {
    Box,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogContent,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { removeAttachment, uploadAttachment } from '../onsite-visit.slice';
import { AttachmentDto } from '../onsite-visit.dto';

export default function AttachmentInput({
    questionId,
    question,
    error,
    onChangeResponse,
    t,
    initialAttachments,
}: {
    questionId: string;
    question: string;
    error: boolean;
    onChangeResponse: (id: string, value: string) => void;
    t: (key: string) => string;
    initialAttachments?: AttachmentDto[];
}) {
    const dispatch = useAppDispatch();
    const uploadStatus = useAppSelector((state) => state.onsiteVisit.uploadStatus[questionId]);
    const reduxAttachments = useAppSelector((state) => state.onsiteVisit.uploadedAttachments[questionId]) || [];
    const removedAttachments = useAppSelector((state) => state.onsiteVisit.removedAttachments[questionId]) || [];
    const allAttachments: (AttachmentDto & { isTmp?: boolean })[] = [
        ...(initialAttachments ?? []),
        ...reduxAttachments,
    ];
    const visibleAttachments = allAttachments.filter((a) => !removedAttachments.includes(a.externalId));

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const handleAddPhoto = async (files: FileList | null) => {
        if (!files) return;

        for (const file of Array.from(files)) {
            try {
                await dispatch(uploadAttachment({ file, questionId })).unwrap();
            } catch (err) {
                console.error('Upload failed', err);
            }
        }
    };

    const handleRemovePhoto = (externalId: string) => {
        dispatch(removeAttachment({ questionId, externalId }));
    };
    const [lastCount, setLastCount] = useState<number | null>(null);

    useEffect(() => {
        if (visibleAttachments.length !== lastCount) {
            onChangeResponse(questionId, visibleAttachments.length.toString());
            setLastCount(visibleAttachments.length);
        }
    }, [visibleAttachments.length, questionId, onChangeResponse, lastCount]);

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">{question}</Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: 2,
                    mt: 1,
                }}
            >
                {visibleAttachments.map((attachment, index) => (
                    <Box
                        key={index}
                        sx={{
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: 1,
                            backgroundColor: '#f0f0f0',
                        }}
                    >
                        <img
                            src={attachment.appUrl}
                            alt={attachment.name ?? `Photo ${index + 1}`}
                            onClick={() => setPreviewUrl(attachment.appUrl)}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer',
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => handleRemovePhoto(attachment.externalId)}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            <Button variant="contained" component="label" sx={{ mt: 2 }} disabled={uploadStatus?.isLoading}>
                {uploadStatus?.isLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : t('Add Photo')}
                <input hidden accept="image/*" type="file" multiple onChange={(e) => handleAddPhoto(e.target.files)} />
            </Button>

            {uploadStatus?.type === 'error' && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {uploadStatus.message || t('Upload failed')}
                </Typography>
            )}

            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {t('Please upload at least one file')}
                </Typography>
            )}

            <Dialog open={!!previewUrl} onClose={() => setPreviewUrl(null)} maxWidth="md">
                <DialogTitle>
                    {t('Preview')}
                    <IconButton
                        onClick={() => setPreviewUrl(null)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="img"
                        src={previewUrl ?? ''}
                        alt="Preview"
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '80vh',
                            display: 'block',
                            margin: '0 auto',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
}
